import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, SlidersHorizontal, X } from "lucide-react";
import { api, CATEGORIES } from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";
import { SectionTag } from "@/components/Section";

export default function Catalog() {
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(params.get("q") || "");
  const category = params.get("category") || "";
  const brand = params.get("brand") || "";
  const inStock = params.get("in_stock") === "1";

  const update = (patch) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
    setParams(next);
  };

  useEffect(() => { api.get("/brands").then((r) => setBrands(r.data)); }, []);
  useEffect(() => {
    setLoading(true);
    const t = setTimeout(() => {
      api.get("/products", { params: { q: q || undefined, category: category || undefined, brand: brand || undefined, in_stock: inStock || undefined } })
        .then((r) => setProducts(r.data)).finally(() => setLoading(false));
    }, 200);
    return () => clearTimeout(t);
  }, [q, category, brand, inStock]);

  useEffect(() => { const t = setTimeout(() => update({ q }), 300); return () => clearTimeout(t); }, [q]); // eslint-disable-line

  const active = useMemo(() => [category, brand, inStock ? "in_stock" : ""].filter(Boolean).length, [category, brand, inStock]);

  return (
    <div data-testid="catalog-page">
      <section className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-12">
          <SectionTag>Wholesale catalog</SectionTag>
          <h1 className="mt-4 text-4xl sm:text-5xl font-extrabold uppercase tracking-tight text-[#0B1F4B]">Product <span className="text-[#1E7CF2]">catalog</span></h1>
          <div className="mt-6 relative max-w-2xl">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products, brands, flavors, sizes…" data-testid="catalog-search-input"
              className="sr-input pl-12 py-4 text-base rounded-full" />
            {q && <button onClick={() => setQ("")} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-[#0B1F4B]" data-testid="catalog-search-clear"><X size={16} /></button>}
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 lg:px-8 py-10 grid lg:grid-cols-12 gap-8">
        <aside className="lg:col-span-3">
          <div className="sr-card p-5 lg:sticky lg:top-28 space-y-6">
            <div className="flex items-center justify-between">
              <p className="sr-label flex items-center gap-2"><SlidersHorizontal size={14} /> Filters</p>
              {active > 0 && <button onClick={() => update({ category: "", brand: "", in_stock: "" })} className="text-xs text-[#E63946] font-semibold" data-testid="catalog-clear-filters">Clear ({active})</button>}
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-[#0B1F4B] mb-2">Category</p>
              <div className="flex flex-wrap gap-2">
                {["", ...CATEGORIES].map((c) => (
                  <button key={c} onClick={() => update({ category: c })} data-testid={`category-filter-${(c || "all").toLowerCase()}`}
                    className={`rounded-full px-3 py-1.5 text-xs font-semibold border transition-colors ${category === c ? "bg-[#0B1F4B] text-white border-[#0B1F4B]" : "border-slate-300 text-slate-600 hover:border-[#1E7CF2] hover:text-[#1E7CF2]"}`}>{c || "All"}</button>
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-[#0B1F4B] mb-2">Brand</p>
              <div className="flex flex-col gap-1">
                <button onClick={() => update({ brand: "" })} data-testid="brand-filter-all" className={`text-left text-sm px-3 py-1.5 rounded-lg ${!brand ? "bg-[#EBF3FE] text-[#1E40AF] font-semibold" : "text-slate-600 hover:bg-slate-50"}`}>All brands</button>
                {brands.map((b) => (
                  <button key={b.slug} onClick={() => update({ brand: b.slug })} data-testid={`brand-filter-${b.slug}`}
                    className={`text-left text-sm px-3 py-1.5 rounded-lg flex justify-between ${brand === b.slug ? "bg-[#EBF3FE] text-[#1E40AF] font-semibold" : "text-slate-600 hover:bg-slate-50"}`}>
                    <span>{b.name}</span><span className="text-xs text-slate-400 font-mono">{b.product_count}</span>
                  </button>
                ))}
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
              <input type="checkbox" checked={inStock} onChange={(e) => update({ in_stock: e.target.checked ? "1" : "" })} data-testid="in-stock-filter" className="accent-[#1E7CF2]" />
              Confirmed in stock only
            </label>
          </div>
        </aside>

        <section className="lg:col-span-9">
          <div className="flex items-center justify-between mb-5 text-sm text-slate-500">
            <span data-testid="catalog-result-count">{loading ? "Loading…" : `${products.length} product${products.length === 1 ? "" : "s"}`}{category && ` · ${category}`}{brand && ` · ${brands.find((b) => b.slug === brand)?.name || brand}`}</span>
            <span className="text-xs uppercase tracking-wider text-[#1E7CF2] font-semibold">Pricing on request until confirmed</span>
          </div>
          {!loading && products.length === 0 ? (
            <div className="sr-card p-12 text-center text-slate-500" data-testid="catalog-empty-state">No products match your search. Try a different keyword or clear filters.</div>
          ) : (
            <div className="grid gap-6 sm:grid-cols-2 xl:grid-cols-3">{products.map((p) => <ProductCard key={p.product_id} p={p} />)}</div>
          )}
        </section>
      </div>
    </div>
  );
}
